/**
 * API client.
 *
 * Access tokens are short-lived, so a 401 triggers exactly one refresh attempt
 * and the original request is replayed. Concurrent 401s share a single refresh
 * promise — otherwise a dashboard with a dozen widgets fires a dozen refreshes
 * and rotates the token out from under itself.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const ACCESS_KEY = 'itr.access';
const REFRESH_KEY = 'itr.refresh';
const SERVER_KEY = 'itr.server';

/**
 * API base URL.
 *
 * Empty means same-origin, which is how the browser portal is served (Vite
 * proxies `/api` in dev; the server serves the built bundle in production).
 *
 * The desktop shell is different: a bundled Tauri app loads from
 * `tauri://localhost`, so a relative `/api/...` would resolve against the
 * webview's own origin and never reach the server. There the user picks a
 * server on the login screen and it is stored here.
 */
let apiBase = localStorage.getItem(SERVER_KEY) ?? (import.meta.env.VITE_API_BASE ?? '');

/** True when running inside the Tauri desktop shell rather than a browser. */
export function isDesktopShell(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
}

export const server = {
  get base(): string {
    return apiBase;
  },
  /** Persisted so the desktop app remembers the server across launches. */
  set(url: string): void {
    apiBase = url.replace(/\/+$/, '');
    if (apiBase) localStorage.setItem(SERVER_KEY, apiBase);
    else localStorage.removeItem(SERVER_KEY);
  },
  /** The desktop shell cannot fall back to same-origin — it has no server. */
  get required(): boolean {
    return isDesktopShell() && !apiBase;
  },
};

/** Absolute URL for a server path, honouring the configured base. */
export function apiUrl(path: string): string {
  return apiBase ? `${apiBase}${path}` : path;
}

/**
 * Where the session's tokens live.
 *
 * In a browser served by the server itself, the refresh token is an HttpOnly
 * cookie this code never sees, and the access token lives only in memory — so
 * nothing a script on the page could read outlives the tab, and the long-lived
 * credential is never readable at all. Every page load starts by trading the
 * cookie for a fresh access token.
 *
 * The desktop shell cannot do that: its `tauri://` origin makes every request
 * cross-site, so a SameSite cookie never arrives. It keeps both tokens in
 * localStorage as before.
 */
function cookieSession(): boolean {
  return !isDesktopShell() && !apiBase;
}

/** Not a secret — only records that a cookie session exists, so a signed-out visitor is not sent to /refresh on every load. */
const SESSION_HINT_KEY = 'itr.session';

let accessToken: string | null = cookieSession() ? null : localStorage.getItem(ACCESS_KEY);
let refreshToken: string | null = localStorage.getItem(REFRESH_KEY);

/**
 * "Connect as": an administrator testing another user's role. The short token
 * the server issued lives in sessionStorage — this tab only, gone when it
 * closes — and is sent instead of the administrator's own access token, whose
 * cookie session stays untouched underneath. Returning is just dropping it.
 */
const ACTING_KEY = 'itr.connect-as';
let actingToken: string | null = null;
try {
  actingToken = sessionStorage.getItem(ACTING_KEY);
} catch {
  actingToken = null;
}

/** The bearer token for the next request: the connect-as token when there is one. */
function bearer(): string | null {
  return actingToken ?? accessToken;
}
let refreshPromise: Promise<boolean> | null = null;
let onUnauthenticated: (() => void) | null = null;

// A browser signed in before cookie sessions still holds its refresh token in
// localStorage. It is presented once, in the body, on the first refresh below;
// the server answers with the cookie, and the stored copy is deleted.
if (cookieSession()) localStorage.removeItem(ACCESS_KEY);

export const tokens = {
  /** The token requests go out with: the connect-as token while connected as someone else. */
  get access() {
    return bearer();
  },
  /** Whether there may be a session to resume — decides if a load tries /refresh at all. */
  get hasSession() {
    if (accessToken || refreshToken) return true;
    try {
      return cookieSession() && localStorage.getItem(SESSION_HINT_KEY) === '1';
    } catch {
      return false;
    }
  },
  /** The body-borne refresh token, for clients that are not cookie sessions (or a legacy one awaiting migration). */
  get refresh() {
    return refreshToken;
  },
  set(access: string, refresh?: string) {
    accessToken = access;
    if (cookieSession()) {
      refreshToken = null;
      localStorage.removeItem(REFRESH_KEY);
      localStorage.setItem(SESSION_HINT_KEY, '1');
      return;
    }
    if (refresh) refreshToken = refresh;
    localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    accessToken = null;
    refreshToken = null;
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(SESSION_HINT_KEY);
  },
  onUnauthenticated(handler: () => void) {
    onUnauthenticated = handler;
  },
  /** Whether this tab is connected as another user. */
  get connectedAs() {
    return actingToken !== null;
  },
  /** Start acting as another user with the token the server issued; the page reloads as them. */
  connectAs(token: string) {
    actingToken = token;
    try {
      sessionStorage.setItem(ACTING_KEY, token);
    } catch {
      /* no sessionStorage: the token still works until the tab reloads */
    }
  },
  /** Back to the administrator's own session. */
  returnToSelf() {
    actingToken = null;
    try {
      sessionStorage.removeItem(ACTING_KEY);
    } catch {
      /* ignore */
    }
  },
};

/** Tells the server this client wants its refresh token as a cookie. */
function sessionHeaders(): Record<string, string> {
  return cookieSession() ? { 'X-ITR-Session': 'cookie' } : {};
}

async function doRefresh(): Promise<boolean> {
  try {
    const res = await fetch(apiUrl('/api/auth/refresh'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...sessionHeaders() },
      credentials: 'same-origin',
      body: JSON.stringify(refreshToken ? { refreshToken } : {}),
    });
    if (!res.ok) {
      tokens.clear();
      onUnauthenticated?.();
      return false;
    }
    const data = (await res.json()) as { accessToken: string; refreshToken?: string };
    tokens.set(data.accessToken, data.refreshToken);
    return true;
  } catch {
    return false;
  }
}

export async function refreshAccessToken(): Promise<boolean> {
  if (!tokens.hasSession) return false;

  refreshPromise ??= (async () => {
    try {
      // Every refresh rotates the cookie, and all tabs share it: two tabs
      // refreshing at once would both present the same token, and the second
      // would find it already revoked and sign out. The lock makes them take
      // turns, so the later tab sends the cookie the earlier one just received.
      const locks = cookieSession() ? navigator.locks : undefined;
      return locks ? await locks.request('itr-refresh', doRefresh) : await doRefresh();
    } finally {
      // Clear on the next tick so everyone awaiting this round shares it, but
      // a later 401 starts a fresh attempt.
      setTimeout(() => {
        refreshPromise = null;
      }, 0);
    }
  })();

  return refreshPromise;
}

async function request<T>(method: string, path: string, body?: unknown, retry = true): Promise<T> {
  const headers: Record<string, string> = { ...sessionHeaders() };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  { const t = bearer(); if (t) headers.Authorization = `Bearer ${t}`; }

  const res = await fetch(apiUrl(path), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 401 && actingToken) {
    // The connect-as token ran out (or the account went away): back to the administrator.
    tokens.returnToSelf();
    window.location.reload();
    throw new ApiError(401, 'The connect-as session has ended; returning to your own account.', 'unauthenticated');
  }
  if (res.status === 401 && retry && tokens.hasSession) {
    if (await refreshAccessToken()) return request<T>(method, path, body, false);
    throw new ApiError(401, 'Your session has expired. Please sign in again.', 'unauthenticated');
  }

  if (!res.ok) {
    const payload = await res.json().catch(() => ({}) as { error?: string; code?: string });
    throw new ApiError(res.status, payload.error ?? `Request failed (${res.status})`, payload.code);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, params?: Record<string, unknown>) => {
    const query = params ? `?${buildQuery(params)}` : '';
    return request<T>('GET', `${path}${query}`);
  },
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

/**
 * POST a request and consume an NDJSON stream of events.
 *
 * Used by the diagnostics, where a traceroute takes half a minute and hops must
 * appear as they resolve. Deliberately not EventSource: that is GET-only and
 * cannot carry an Authorization header, so the token would have to go in the
 * query string and end up in access logs.
 */
export async function streamNdjson<T>(
  path: string,
  body: unknown,
  onEvent: (event: T) => void,
  signal?: AbortSignal,
): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  { const t = bearer(); if (t) headers.Authorization = `Bearer ${t}`; }

  const res = await fetch(apiUrl(path), { method: 'POST', headers, body: JSON.stringify(body), signal });

  if (!res.ok) {
    // A pre-stream rejection (validation, permissions, rate limit) is plain JSON.
    const payload = await res.json().catch(() => ({}) as { error?: string; code?: string });
    throw new ApiError(res.status, payload.error ?? `Request failed (${res.status})`, payload.code);
  }
  if (!res.body) throw new ApiError(500, 'The server returned no stream');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    // The last element is a partial line unless the chunk ended exactly on a
    // newline; keep it for the next read either way.
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.trim()) continue;
      try {
        onEvent(JSON.parse(line) as T);
      } catch {
        /* a truncated frame is not worth aborting the run for */
      }
    }
  }
  if (buffer.trim()) {
    try {
      onEvent(JSON.parse(buffer) as T);
    } catch {
      /* ignore trailing partial */
    }
  }
}

/**
 * Fetch a binary response (an image, say) with the same auth header a normal
 * `api.get` would send.
 *
 * A plain `<img src>` can't carry `Authorization` — only JS-driven fetches
 * can — so a `Preview` panel needs the bytes itself rather than a URL to hand
 * the browser. Returns an object URL; the caller must `URL.revokeObjectURL`
 * it once it's no longer displayed, or each poll leaks memory.
 */
export async function apiFetchBlobUrl(path: string, signal?: AbortSignal): Promise<string> {
  const headers: Record<string, string> = {};
  { const t = bearer(); if (t) headers.Authorization = `Bearer ${t}`; }

  const res = await fetch(apiUrl(path), { headers, signal });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}) as { error?: string; code?: string });
    throw new ApiError(res.status, payload.error ?? `Request failed (${res.status})`, payload.code);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/**
 * Open an authenticated GET as a byte stream — live audio from a listen-in,
 * read chunk by chunk as the server produces it. Abort `signal` to hang up.
 */
export async function apiStream(path: string, signal?: AbortSignal): Promise<ReadableStream<Uint8Array>> {
  const headers: Record<string, string> = {};
  { const t = bearer(); if (t) headers.Authorization = `Bearer ${t}`; }

  const res = await fetch(apiUrl(path), { headers, signal });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}) as { error?: string; code?: string });
    throw new ApiError(res.status, payload.error ?? `Request failed (${res.status})`, payload.code);
  }
  if (!res.body) throw new ApiError(500, 'The server returned no stream');
  return res.body;
}

/**
 * Download an authenticated file and hand it to the browser as a save.
 *
 * Same reason as `apiFetchBlobUrl`: a plain link can't carry the bearer token.
 * The name comes from the server's Content-Disposition when it sends one.
 */
export async function apiDownload(path: string, fallbackName: string): Promise<void> {
  const url = await apiFetchBlobUrlWithName(path);
  const link = document.createElement('a');
  link.href = url.href;
  link.download = url.filename ?? fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Give the browser a moment to start the save before the blob goes away.
  setTimeout(() => URL.revokeObjectURL(url.href), 60_000);
}

async function apiFetchBlobUrlWithName(path: string): Promise<{ href: string; filename: string | null }> {
  const headers: Record<string, string> = {};
  { const t = bearer(); if (t) headers.Authorization = `Bearer ${t}`; }

  const res = await fetch(apiUrl(path), { headers });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}) as { error?: string; code?: string });
    throw new ApiError(res.status, payload.error ?? `Request failed (${res.status})`, payload.code);
  }
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  const plain = /filename="([^"]+)"/i.exec(disposition)?.[1];
  const filename = encoded ? decodeURIComponent(encoded) : plain ?? null;
  return { href: URL.createObjectURL(await res.blob()), filename };
}

function buildQuery(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }
  return search.toString();
}
